import axios from 'axios';

// Use relative path in production, localhost in development
const API_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

const api = axios.create({
  baseURL: API_URL,
  timeout: 10000, // 10 second timeout
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle trial/subscription errors and 401s globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const code = error.response?.data?.code;

    // Auto-logout on 401 (expired / invalid token)
    if (status === 401) {
      localStorage.removeItem('token');
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    if (code === 'TRIAL_READ_ONLY') {
      window.dispatchEvent(new CustomEvent('trial-read-only', {
        detail: { message: error.response.data.message },
      }));
    }
    if (code === 'ACCOUNT_EXPIRED') {
      window.dispatchEvent(new CustomEvent('account-expired', {
        detail: { message: error.response.data.message },
      }));
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  getCurrentUser: () => api.get('/auth/me'),
  inviteUser: (userData) => api.post('/auth/invite', userData),
};

// Routes API
export const routesAPI = {
  getAll: (params) => api.get('/routes', { params }),
  getById: (id) => api.get(`/routes/${id}`),
  getNextNumber: () => api.get('/routes/next/number'),
  getFilterOptions: () => api.get('/routes/filters/options'),
  create: (routeData) => api.post('/routes', routeData),
  update: (id, routeData) => api.put(`/routes/${id}`, routeData),
  delete: (id) => api.delete(`/routes/${id}`)
};

// Optimization API
export const optimizeAPI = {
  optimize: (routeId) => api.post('/optimize', { routeId }),
  suggest: (routeData) => api.post('/optimize/suggest', routeData),
};

// Geocoding API
export const geocodeAPI = {
  geocode: (address) => api.post('/geocode/geocode', { address }),
  distance: (origin, destination) => api.post('/geocode/distance', { origin, destination }),
};

// Schedule API
export const scheduleAPI = {
  schedule: (routeIds, options) => api.post('/schedule/schedule', { routeIds, options }),
  analyze: (startDate, endDate) => api.post('/schedule/analyze', { startDate, endDate }),
  suggestTruck: (routeId) => api.post('/schedule/suggest-truck', { routeId }),
  assignTruck: (routeId, truckId) => api.post('/schedule/assign-truck', { routeId, truckId }),
  dispatchRoute: (routeId, notes) => api.post('/schedule/dispatch-route', { routeId, notes }),
  startRoute: (routeId) => api.post('/schedule/start-route', { routeId }),
  unassignTruck: (routeId) => api.post('/schedule/unassign-truck', { routeId }),
};

// Trucks API
export const trucksAPI = {
  getAll: (params) => api.get('/trucks', { params }),
  getOne: (id) => api.get(`/trucks/${id}`),
  getFilterOptions: () => api.get('/trucks/filters/options'),
  create: (truckData) => api.post('/trucks', truckData),
  update: (id, truckData) => api.put(`/trucks/${id}`, truckData),
  delete: (id) => api.delete(`/trucks/${id}`),
  getAvailable: () => api.get('/trucks/available/list'),
  assign: (id, routeId) => api.post(`/trucks/${id}/assign`, { routeId }),
  unassign: (id) => api.post(`/trucks/${id}/unassign`),
};

// Customers API
export const customersAPI = {
  getAll: () => api.get('/customers'),
  getOne: (id) => api.get(`/customers/${id}`),
  create: (customerData) => api.post('/customers', customerData),
  update: (id, customerData) => api.put(`/customers/${id}`, customerData),
  delete: (id) => api.delete(`/customers/${id}`)
};

export const freightTypesAPI = {
  getAll: () => api.get('/freight-types'),
  getOne: (id) => api.get(`/freight-types/${id}`),
  create: (freightTypeData) => api.post('/freight-types', freightTypeData),
  update: (id, freightTypeData) => api.put(`/freight-types/${id}`, freightTypeData),
  delete: (id) => api.delete(`/freight-types/${id}`)
};

export const driversAPI = {
  getAll: () => api.get('/drivers'),
  getOne: (id) => api.get(`/drivers/${id}`),
  create: (driverData) => api.post('/drivers', driverData),
  update: (id, driverData) => api.put(`/drivers/${id}`, driverData),
  delete: (id) => api.delete(`/drivers/${id}`),
  assignTruck: (id, truckId) => api.post(`/drivers/${id}/assign-truck`, { truckId }),
  unassignTruck: (id) => api.post(`/drivers/${id}/unassign-truck`)
};

export const tripStopsAPI = {
  getAll: (params) => api.get('/trip-stops', { params }),
  getByRoute: (routeId) => api.get(`/trip-stops/route/${routeId}`),
  getOne: (id) => api.get(`/trip-stops/${id}`),
  create: (stopData) => api.post('/trip-stops', stopData),
  update: (id, stopData) => api.put(`/trip-stops/${id}`, stopData),
  updateStatus: (id, statusData) => api.patch(`/trip-stops/${id}/status`, statusData),
  addProofOfDelivery: (id, proofData) => api.patch(`/trip-stops/${id}/proof-of-delivery`, proofData),
  delete: (id) => api.delete(`/trip-stops/${id}`)
};

// Loads API (Dispatch Board)
export const loadsAPI = {
  getAll: (params) => api.get('/loads', { params }),
  getBoard: (params) => api.get('/loads/board', { params }),
  getBoardUpdates: (since) => api.get('/loads/board/updates', { params: { since } }),
  getStats: () => api.get('/loads/stats'),
  getFilterOptions: () => api.get('/loads/filters/options'),
  getOne: (id) => api.get(`/loads/${id}`),
  create: (loadData) => api.post('/loads', loadData),
  update: (id, loadData) => api.put(`/loads/${id}`, loadData),
  updateStatus: (id, status) => api.patch(`/loads/${id}/status`, { status }),
  assignTruck: (id, data) => api.patch(`/loads/${id}/assign`, data),
  respond: (id, action, reason) => api.patch(`/loads/${id}/respond`, { action, reason }),
  updateNotes: (id, data) => api.patch(`/loads/${id}/notes`, data),
  delete: (id) => api.delete(`/loads/${id}`),
  // Deadhead
  getDeadhead: (driverName, originCity, originState) =>
    api.get(`/loads/deadhead/${encodeURIComponent(driverName)}`, { params: { originCity, originState } }),
  // Lane Templates
  getTemplates: () => api.get('/loads/templates'),
  saveTemplate: (data) => api.post('/loads/templates', data),
  deleteTemplate: (id) => api.delete(`/loads/templates/${id}`),
  // Documents
  getDocuments: (id) => api.get(`/loads/${id}/documents`),
  uploadDocument: (id, doc) => api.post(`/loads/${id}/documents`, doc),
  downloadDocument: (id, docId) => api.get(`/loads/${id}/documents/${docId}/download`),
  deleteDocument: (id, docId) => api.delete(`/loads/${id}/documents/${docId}`),
};

// Messages API (Dispatch Messaging)
export const messagesAPI = {
  getAll: (params) => api.get('/messages', { params }),
  getThreads: () => api.get('/messages/threads'),
  getUnreadCount: () => api.get('/messages/unread-count'),
  getByLoad: (loadId) => api.get(`/messages/load/${loadId}`),
  send: (data) => api.post('/messages', data),
  markRead: (id) => api.patch(`/messages/${id}/read`),
  markThreadRead: (loadId) => api.patch(`/messages/load/${loadId}/read-all`),
  delete: (id) => api.delete(`/messages/${id}`),
  getWhatsAppStatus: () => api.get('/messages/whatsapp-status'),
};

// Settings / Admin Config API
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  updateSection: (section, data) => api.patch(`/settings/${section}`, data),
  uploadLogo: (base64) => api.post('/settings/logo', { logo: base64 }),
  removeLogo: () => api.delete('/settings/logo'),
  getLogo: () => api.get('/settings/logo'),
  getUsers: () => api.get('/settings/users'),
  updateUserRole: (userId, role) => api.patch(`/settings/users/${userId}/role`, { role }),
  removeUser: (userId) => api.delete(`/settings/users/${userId}`),
  getOnboarding: () => api.get('/settings/onboarding'),
  dismissOnboarding: () => api.post('/settings/onboarding/dismiss'),
};

// Invoice API
export const invoiceAPI = {
  getPDF: (loadId) => api.get(`/invoices/${loadId}/pdf`, { responseType: 'blob' }),
  sendEmail: (loadId) => api.post(`/invoices/${loadId}/email`),
};

// EDI API
export const ediAPI = {
  getTransactions: (params) => api.get('/edi/transactions', { params }),
  getTransaction: (id) => api.get(`/edi/transactions/${id}`),
  send990: (loadId, accept, reason) => api.post(`/edi/990/${loadId}`, { accept, reason }),
  send214: (loadId, status) => api.post(`/edi/214/${loadId}`, { status }),
  send210: (loadId) => api.post(`/edi/210/${loadId}`),
  testConnection: () => api.post('/edi/test-connection'),
};

// Carrier API (multi-carrier management)
export const carrierAPI = {
  getAll: (params) => api.get('/carriers', { params }),
  getOne: (id) => api.get(`/carriers/${id}`),
  create: (data) => api.post('/carriers', data),
  update: (id, data) => api.put(`/carriers/${id}`, data),
  delete: (id) => api.delete(`/carriers/${id}`),
};

// Fleet Guard API (carrier compliance monitoring)
export const fleetGuardAPI = {
  // FMCSA lookups (no carrier record needed)
  lookupDOT: (dot) => api.get(`/fleetguard/fmcsa/lookup/dot/${dot}`),
  lookupName: (name) => api.get(`/fleetguard/fmcsa/lookup/name/${encodeURIComponent(name)}`),
  lookupMC: (mc) => api.get(`/fleetguard/fmcsa/lookup/mc/${mc}`),

  // Tracked Companies (primary Fleet Guard entity)
  getTrackedCompanies: (params) => api.get('/fleetguard/companies', { params }),
  getTrackedCompany: (id) => api.get(`/fleetguard/companies/${id}`),
  saveTrackedCompany: (data) => api.post('/fleetguard/companies', data),
  lookupAndSave: (data) => api.post('/fleetguard/companies/lookup-and-save', data),
  updateTrackedCompany: (id, data) => api.put(`/fleetguard/companies/${id}`, data),
  refreshTrackedCompany: (id) => api.post(`/fleetguard/companies/${id}/refresh`),
  pullTrackedCompanyData: (id) => api.post(`/fleetguard/companies/${id}/pull`),
  removeTrackedCompany: (id) => api.delete(`/fleetguard/companies/${id}`),

  // Watchlist (legacy)
  getWatchlist: (params) => api.get('/fleetguard/watchlist', { params }),
  addToWatchlist: (data) => api.post('/fleetguard/watchlist', data),
  removeFromWatchlist: (id) => api.delete(`/fleetguard/watchlist/${id}`),
  // FMCSA data (requires carrier in DB)
  pullFMCSA: (carrierId) => api.post(`/fleetguard/fmcsa/pull/${carrierId}`),
  getSaferLatest: (carrierId) => api.get(`/fleetguard/fmcsa/safer/${carrierId}/latest`),
  getInsuranceLatest: (carrierId) => api.get(`/fleetguard/fmcsa/insurance/${carrierId}/latest`),
  // Compliance
  getCompliance: (params) => api.get('/fleetguard/compliance', { params }),
  // Alerts
  getAlerts: (params) => api.get('/fleetguard/alerts', { params }),
  getAlertCounts: () => api.get('/fleetguard/alerts/counts'),
  acknowledgeAlert: (id) => api.patch(`/fleetguard/alerts/${id}/acknowledge`),
  resolveAlert: (id) => api.patch(`/fleetguard/alerts/${id}/resolve`),
  dismissAlert: (id) => api.patch(`/fleetguard/alerts/${id}/dismiss`),
  // Risk
  getRiskScore: (carrierId) => api.get(`/fleetguard/risk/${carrierId}`),
  calculateRisk: (carrierId) => api.post(`/fleetguard/risk/${carrierId}/calculate`),
  getRanking: () => api.get('/fleetguard/risk/ranking'),
  // Safety
  getInspections: (carrierId) => api.get(`/fleetguard/safety/inspections/${carrierId}`),
  getCrashes: (carrierId) => api.get(`/fleetguard/safety/crashes/${carrierId}`),
  getSafetySummary: (carrierId) => api.get(`/fleetguard/safety/summary/${carrierId}`),
  // Documents
  uploadDocument: (carrierId, formData) => api.post(`/fleetguard/documents/${carrierId}`, formData),
  getDocuments: (params) => api.get('/fleetguard/documents', { params }),
  downloadDocument: (id) => api.get(`/fleetguard/documents/${id}/download`),
  // Affiliations
  getAffiliations: (params) => api.get('/fleetguard/affiliations', { params }),
  createAffiliation: (data) => api.post('/fleetguard/affiliations', data),
  updateAffiliation: (id, data) => api.put(`/fleetguard/affiliations/${id}`, data),
  verifyAffiliation: (id) => api.patch(`/fleetguard/affiliations/${id}/verify`),
  addEvidence: (id, data) => api.post(`/fleetguard/affiliations/${id}/evidence`, data),
  deleteAffiliation: (id) => api.delete(`/fleetguard/affiliations/${id}`),
  crossRef: (entityName) => api.get(`/fleetguard/affiliations/cross-ref/${encodeURIComponent(entityName)}`),

  // ── Business Entities ──
  getEntities: (params) => api.get('/fleetguard/entities', { params }),
  getEntity: (id) => api.get(`/fleetguard/entities/${id}`),
  createEntity: (data) => api.post('/fleetguard/entities', data),
  updateEntity: (id, data) => api.put(`/fleetguard/entities/${id}`, data),
  deleteEntity: (id) => api.delete(`/fleetguard/entities/${id}`),
  // Persons
  getPersons: (params) => api.get('/fleetguard/entities/persons/list', { params }),
  createPerson: (data) => api.post('/fleetguard/entities/persons', data),
  updatePerson: (id, data) => api.put(`/fleetguard/entities/persons/${id}`, data),
  deletePerson: (id) => api.delete(`/fleetguard/entities/persons/${id}`),
  // Person ↔ Entity links
  linkPerson: (entityId, data) => api.post(`/fleetguard/entities/${entityId}/persons`, data),
  unlinkPerson: (entityId, personId) => api.delete(`/fleetguard/entities/${entityId}/persons/${personId}`),

  // ── Relationship graph ──
  getRelationships: (params) => api.get('/fleetguard/relationships', { params }),
  getRelationship: (id) => api.get(`/fleetguard/relationships/${id}`),
  getEntityRelationships: (entityId) => api.get(`/fleetguard/relationships/for/${entityId}`),
  getLeasingCompanies: (carrierId) => api.get(`/fleetguard/relationships/leasing/${carrierId}`),
  createRelationship: (data) => api.post('/fleetguard/relationships', data),
  addRelationshipEvidence: (relId, data) => api.post(`/fleetguard/relationships/${relId}/evidence`, data),
  removeRelationshipEvidence: (relId, evId) => api.delete(`/fleetguard/relationships/${relId}/evidence/${evId}`),
  confirmRelationship: (id) => api.patch(`/fleetguard/relationships/${id}/confirm`),
  dismissRelationship: (id) => api.patch(`/fleetguard/relationships/${id}/dismiss`),
  reviewRelationship: (id) => api.patch(`/fleetguard/relationships/${id}/review`),
  deleteRelationship: (id) => api.delete(`/fleetguard/relationships/${id}`),

  // ── Entity Resolution ──
  runEntityResolution: () => api.post('/fleetguard/entity-resolution/run'),
  runCarrierResolution: (carrierId) => api.post(`/fleetguard/entity-resolution/run/${carrierId}`),
  entitySearch: (q) => api.get('/fleetguard/entity-resolution/search', { params: { q } }),

  // Admin
  runScan: () => api.post('/fleetguard/admin/run-scan'),
  bulkSync: () => api.post('/fleetguard/admin/bulk-sync'),
};

// Subscription / Trial API
export const subscriptionAPI = {
  getStatus: () => api.get('/subscription'),
  checkout: (plan) => api.post('/subscription/checkout', { plan }),
  portal: () => api.post('/subscription/portal'),
  getPayments: () => api.get('/subscription/payments'),
};

// Master Admin API
export const adminAPI = {
  getCompanies: () => api.get('/admin/companies'),
  getCompany: (id) => api.get(`/admin/companies/${id}`),
  updateCompany: (id, data) => api.patch(`/admin/companies/${id}`, data),
  deleteCompany: (id, confirmName) => api.delete(`/admin/companies/${id}`, { data: { confirmName } }),
  impersonate: (userId) => api.post(`/admin/impersonate/${userId}`),
  getStats: () => api.get('/admin/stats'),
  resetTrial: (id, days) => api.post(`/admin/companies/${id}/reset-trial`, { days }),
};

// Import API (Excel upload)
// NOTE: Do NOT set Content-Type manually — axios auto-detects multipart/form-data
// with the correct boundary when sending FormData.
export const importAPI = {
  preview: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/import/preview', fd, { timeout: 30000 });
  },
  importCustomers: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/import/customers', fd, { timeout: 60000 });
  },
  importDrivers: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/import/drivers', fd, { timeout: 60000 });
  },
  importTrucks: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/import/trucks', fd, { timeout: 60000 });
  },
  importLoads: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/import/loads', fd, { timeout: 60000 });
  },
  downloadTemplate: (type) => api.get(`/import/template/${type}`, { responseType: 'blob' }),
};

export default api;
